import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Scale, ChevronRight, CheckCircle, ArrowLeft,
  Sparkles, Phone, Shield, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Option sets ───────────────────────────────────────────────

const ATTORNEY_OPTIONS = [
  { value: "Yes", label: "Yes, I have", sub: "Already spoken with one" },
  { value: "No", label: "No, I haven't", sub: "Haven't consulted yet" },
];

const BUDGET_OPTIONS = [
  { value: "Under $1,000", label: "Under $1,000" },
  { value: "$1,000 – $5,000", label: "$1,000 – $5,000" },
  { value: "$5,000 – $10,000", label: "$5,000 – $10,000" },
  { value: "Open to discuss", label: "Open to discuss" },
];

const CALL_TIME_OPTIONS = [
  { value: "9am – 11am", label: "9am – 11am" },
  { value: "11am – 1pm", label: "11am – 1pm" },
  { value: "1pm – 3pm", label: "1pm – 3pm" },
  { value: "3pm – 5pm", label: "3pm – 5pm" },
  { value: "5pm – 7pm", label: "5pm – 7pm" },
  { value: "7pm – 9pm", label: "7pm – 9pm" },
];

// Steps after contact: 1=location, 2=case, 3=analyzing, 4=feedback, 5=attorney, 6=budget, 7=calltime
const VISIBLE_STEPS = 6; // 3 (analyzing) is a transition, not counted

// ── Main component ────────────────────────────────────────────

export default function IntakeFlow() {
  const navigate = useNavigate();

  const [contactDone, setContactDone] = useState(false);
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [contactErrors, setContactErrors] = useState({});

  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    location: "", what_happened: "", when_it_happened: "",
    prior_consultation: "", fee_budget: "", availability: "",
  });
  const [locationError, setLocationError] = useState("");
  const [caseError, setCaseError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const setField = (key, val) => setData(d => ({ ...d, [key]: val }));
  const setContact_ = (key, val) => setContact(c => ({ ...c, [key]: val }));

  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "");
    const cleaned = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (cleaned.length < 10) return raw;
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // ── Contact confirm ───────────────────────────────────────
  const handleContactConfirm = () => {
    const e = {};
    if (!contact.name.trim()) e.name = "Name is required";
    if (contact.phone.replace(/\D/g, "").length < 10) e.phone = "Valid phone number required";
    if (!contact.email.trim() || !contact.email.includes("@")) e.email = "Valid email required";
    setContactErrors(e);
    if (Object.keys(e).length > 0) return;
    setContactDone(true);
  };

  // ── Step handlers ─────────────────────────────────────────
  const handleLocationNext = () => {
    if (!data.location.trim()) { setLocationError("Please enter a city and state"); return; }
    setLocationError("");
    setStep(2);
  };

  const handleCaseNext = async () => {
    if (!data.what_happened.trim()) { setCaseError("Please describe what happened"); return; }
    setCaseError("");
    setStep(3);
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/intake/analyze`, {
        location: data.location,
        what_happened: data.what_happened,
        when_it_happened: data.when_it_happened,
      });
      setFeedback(res.data.feedback || "");
    } catch (err) {
      setFeedback("");
    }
    setAnalyzing(false);
    setStep(4);
  };

  const selectAndAdvance = async (key, value) => {
    const newData = { ...data, [key]: value };
    setData(newData);
    if (step === 7) {
      await submit(newData);
    } else {
      setStep(s => s + 1);
    }
  };

  const submit = async (finalData) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await axios.post(`${API}/intake/submit`, {
        name: contact.name.trim(),
        phone: formatPhone(contact.phone),
        email: contact.email.trim(),
        location: finalData.location.trim(),
        what_happened: finalData.what_happened.trim(),
        when_it_happened: finalData.when_it_happened?.trim() || null,
        prior_consultation: finalData.prior_consultation,
        fee_budget: finalData.fee_budget,
        availability: finalData.availability,
        consent_timestamp: new Date().toISOString(),
      });
      navigate("/intake/success", {
        state: { leadData: res.data.lead_data, sessionId: res.data.lead_id },
      });
    } catch (err) {
      setSubmitError("Something went wrong. Please try again.");
      setSubmitting(false);
      setStep(7);
    }
  };

  // Progress bar — step 3 (analyzing) is a transition, skip it in count
  const visibleStep = step >= 4 ? step - 1 : step;
  const progress = Math.round(Math.min((visibleStep / VISIBLE_STEPS) * 100, 100));

  const showBack = !submitting && !analyzing &&
    (step === 2 || step === 5 || step === 6);

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 5) setStep(4);
    else if (step === 6) setStep(5);
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div
      className="bg-background min-h-[100dvh] flex flex-col items-center px-4"
      style={{ paddingTop: "env(safe-area-inset-top, 24px)", paddingBottom: "env(safe-area-inset-bottom, 32px)" }}
    >
      <Helmet><title>Get Legal Help — Free Intake</title></Helmet>

      {/* Header — always visible */}
      <div className="w-full max-w-md pt-6 pb-4">
        <div className="flex items-center gap-3 mb-5">
          {showBack && (
            <button onClick={handleBack}
              className="p-2 rounded-full hover:bg-secondary/50 transition-colors text-muted-foreground -ml-1">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Free Legal Intake
            </span>
          </div>
        </div>

        {/* Progress bar — hidden on contact screen and analyzing */}
        {contactDone && step !== 3 && !submitting && (
          <>
            <div className="h-1 bg-border/30 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] text-muted-foreground/40">
                Step {visibleStep} of {VISIBLE_STEPS}
              </span>
              <span className="text-[10px] text-muted-foreground/40">{progress}%</span>
            </div>
          </>
        )}
      </div>

      {/* Active screen */}
      <div className="w-full max-w-md">
        {!contactDone && (
          <ContactScreen
            contact={contact}
            setContact={setContact_}
            errors={contactErrors}
            onConfirm={handleContactConfirm}
          />
        )}
        {contactDone && submitting && <SubmittingCard />}
        {contactDone && !submitting && (
          <>
            {step === 1 && (
              <SashaQuestion
                question={`Thanks${contact.name ? `, ${contact.name.split(" ")[0]}` : ""}. First — where did this happen?`}
                hint="City and state is enough"
              >
                <InputField
                  value={data.location}
                  onChange={v => setField("location", v)}
                  placeholder="e.g. Miami, FL"
                  error={locationError}
                  autoComplete="off"
                />
                <PrimaryButton onClick={handleLocationNext} label="Next" />
              </SashaQuestion>
            )}
            {step === 2 && (
              <SashaQuestion
                question="Tell me what happened."
                hint="In your own words — a summary is fine"
              >
                <div className="space-y-3">
                  <div>
                    <textarea
                      value={data.what_happened}
                      onChange={e => setField("what_happened", e.target.value)}
                      placeholder="Describe the situation..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl bg-secondary/40 border border-border/50 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
                    />
                    {caseError && <p className="text-xs text-red-400 mt-1">{caseError}</p>}
                  </div>
                  <InputField
                    value={data.when_it_happened}
                    onChange={v => setField("when_it_happened", v)}
                    placeholder="When did this happen? (optional)"
                    autoComplete="off"
                  />
                </div>
                <PrimaryButton onClick={handleCaseNext} label="Next" />
              </SashaQuestion>
            )}
            {step === 3 && <AnalyzingCard />}
            {step === 4 && (
              <FeedbackCard feedback={feedback} onContinue={() => setStep(5)} />
            )}
            {step === 5 && (
              <TileCard
                title="Have you spoken with an attorney?"
                subtitle="About this specific situation"
                options={ATTORNEY_OPTIONS}
                onSelect={val => selectAndAdvance("prior_consultation", val)}
              />
            )}
            {step === 6 && (
              <TileCard
                title="What's your budget for legal help?"
                subtitle="Attorneys work across all budgets — no judgment"
                options={BUDGET_OPTIONS}
                onSelect={val => selectAndAdvance("fee_budget", val)}
                cols={2}
              />
            )}
            {step === 7 && (
              <>
                {submitError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                    {submitError}
                  </div>
                )}
                <TileCard
                  title="Best time to reach you?"
                  subtitle="We'll call during your preferred window"
                  options={CALL_TIME_OPTIONS}
                  onSelect={val => selectAndAdvance("availability", val)}
                  cols={2}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Contact screen ────────────────────────────────────────────

function ContactScreen({ contact, setContact, errors, onConfirm }) {
  return (
    <div className="space-y-5">
      {/* Promise card */}
      <div className="rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 space-y-3">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-primary flex-shrink-0" />
          <p className="text-sm font-semibold text-foreground">
            Someone will call you within 24 hours.
          </p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          This is a real situation — a real person from our team will call to confirm your information and connect you with an attorney in your area. That's why your contact details matter.
        </p>
        <div className="flex flex-wrap gap-3 pt-1">
          {[
            { icon: Shield, label: "Encrypted & secure" },
            { icon: CheckCircle, label: "100% free" },
            { icon: Clock, label: "Response within 24 hrs" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
              <Icon className="w-3 h-3 text-primary/60" />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Form card */}
      <div className="rounded-2xl border border-border/50 bg-card/30 p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
            Let's get you connected.
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your info so we know who to reach.
          </p>
        </div>

        <div className="space-y-4">
          <FormField label="Full Name" error={errors.name}>
            <input
              type="text"
              value={contact.name}
              onChange={e => setContact("name", e.target.value)}
              placeholder="Jane Smith"
              autoComplete="name"
              className="w-full h-12 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </FormField>
          <FormField label="Phone Number" error={errors.phone}>
            <input
              type="tel"
              value={contact.phone}
              onChange={e => setContact("phone", e.target.value)}
              placeholder="(555) 555-5555"
              autoComplete="tel"
              className="w-full h-12 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </FormField>
          <FormField label="Email Address" error={errors.email}>
            <input
              type="email"
              value={contact.email}
              onChange={e => setContact("email", e.target.value)}
              placeholder="jane@example.com"
              autoComplete="email"
              className="w-full h-12 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
            />
          </FormField>
        </div>

        <button
          onClick={onConfirm}
          className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_0_24px_-5px_rgba(99,102,241,0.5)] active:scale-[0.98]"
        >
          <ChevronRight className="w-4 h-4" /> Get me connected
        </button>
      </div>
    </div>
  );
}

// ── Sasha question step ───────────────────────────────────────

function SashaQuestion({ question, hint, children }) {
  return (
    <div className="space-y-4">
      {/* Sasha bubble */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-xs font-bold text-primary">S</span>
        </div>
        <div className="flex-1 bg-secondary/40 border border-border/40 rounded-2xl rounded-tl-sm px-4 py-3">
          <p className="text-sm font-medium leading-snug">{question}</p>
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
      </div>

      {/* Input area */}
      <div className="space-y-3 pl-11">
        {children}
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────

function FormField({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

function InputField({ value, onChange, placeholder, error, autoComplete, type = "text" }) {
  return (
    <div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 placeholder:text-muted-foreground/40 transition-all"
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function PrimaryButton({ onClick, label = "Next" }) {
  return (
    <button
      onClick={onClick}
      className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.4)] active:scale-[0.98]"
    >
      {label} <ChevronRight className="w-4 h-4" />
    </button>
  );
}

function TileCard({ title, subtitle, options, onSelect, cols = 1 }) {
  return (
    <div className="rounded-2xl border border-border/50 bg-card/30 p-6 space-y-5">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className={`grid gap-3 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className="p-4 rounded-xl border border-border/50 bg-secondary/20 hover:bg-primary/10 hover:border-primary/40 active:scale-[0.97] transition-all text-left"
          >
            <div className="font-semibold text-sm leading-snug">{opt.label}</div>
            {opt.sub && <div className="text-xs text-muted-foreground mt-0.5">{opt.sub}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

function AnalyzingCard() {
  return (
    <div className="rounded-2xl border border-border/50 bg-card/30 p-10 flex flex-col items-center gap-5 text-center">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Sparkles className="w-7 h-7 text-primary animate-pulse" />
      </div>
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
          Reviewing your case...
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Just a moment.</p>
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full bg-primary/50 animate-bounce"
            style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}

function FeedbackCard({ feedback, onContinue }) {
  return (
    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold">Sasha's Take</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Case read</p>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-foreground/90">
        {feedback || "Thanks for sharing that. Based on what you've described, this sounds like something attorneys in your area handle regularly. The details you've shared will give them a solid starting point."}
      </p>
      <button
        onClick={onContinue}
        className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.4)] active:scale-[0.98]"
      >
        Continue <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function SubmittingCard() {
  return (
    <div className="rounded-2xl border border-border/50 bg-card/30 p-10 flex flex-col items-center gap-5 text-center">
      <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Scale className="w-7 h-7 text-primary animate-pulse" />
      </div>
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
          Submitting your intake...
        </h2>
        <p className="text-sm text-muted-foreground mt-1">This only takes a second.</p>
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full bg-primary/50 animate-bounce"
            style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}
